import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { cn } from '@/lib/cn'
import { useCreateSupportTicket } from '@/features/support/hooks/useSupportTickets'
import {
  CATEGORY_OPTIONS,
  MESSAGE_MAX,
  PRIORITY_OPTIONS,
  SUBJECT_MAX,
  SUBJECT_MIN,
  textareaClasses,
} from '@/features/support/supportLabels'
import type {
  TicketCategory,
  TicketDetail,
  TicketPriority,
} from '@/types/support-ticket.types'

const FORM_ID = 'new-support-ticket-form'

export interface NewTicketModalProps {
  open: boolean
  onClose: () => void
  onCreated: (ticket: TicketDetail) => void
}

export function NewTicketModal({ open, onClose, onCreated }: NewTicketModalProps): React.ReactElement {
  const createTicket = useCreateSupportTicket()
  const [subject, setSubject] = useState('')
  const [category, setCategory] = useState<TicketCategory>('question')
  const [priority, setPriority] = useState<TicketPriority>('normal')
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const trimmedSubject = subject.trim()
  const trimmedMessage = message.trim()
  const subjectError =
    trimmedSubject.length < SUBJECT_MIN
      ? `Subject needs at least ${SUBJECT_MIN} characters.`
      : trimmedSubject.length > SUBJECT_MAX
        ? `Subject can be at most ${SUBJECT_MAX} characters.`
        : undefined
  const messageError =
    trimmedMessage.length === 0
      ? 'Tell us what you need help with.'
      : trimmedMessage.length > MESSAGE_MAX
        ? `Message can be at most ${MESSAGE_MAX} characters.`
        : undefined

  const reset = (): void => {
    setSubject('')
    setCategory('question')
    setPriority('normal')
    setMessage('')
    setSubmitted(false)
  }

  const handleClose = (): void => {
    if (createTicket.isPending) return
    onClose()
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setSubmitted(true)
    if (subjectError || messageError || createTicket.isPending) return
    createTicket.mutate(
      { subject: trimmedSubject, category, priority, message: trimmedMessage },
      {
        onSuccess: (ticket) => {
          reset()
          onCreated(ticket)
        },
      }
    )
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New support ticket"
      className="max-w-lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={createTicket.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={createTicket.isPending}
            loadingLabel="Sending ticket"
          >
            Send ticket
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Our team replies on the ticket. You&rsquo;ll get a notification and an email when they do.
        </p>

        <Input
          id="ticket-subject"
          label="Subject"
          required
          value={subject}
          maxLength={SUBJECT_MAX}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="A short summary of the issue"
          error={submitted ? subjectError : undefined}
          hint={submitted && subjectError ? undefined : `${trimmedSubject.length}/${SUBJECT_MAX}`}
          autoFocus
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            id="ticket-category"
            label="Category"
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={(v) => setCategory((v || 'question') as TicketCategory)}
            clearable={false}
          />
          <Select
            id="ticket-priority"
            label="Priority"
            options={PRIORITY_OPTIONS}
            value={priority}
            onChange={(v) => setPriority((v || 'normal') as TicketPriority)}
            clearable={false}
          />
        </div>

        <div>
          <label
            htmlFor="ticket-message"
            className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
          >
            Message <span className="text-danger">*</span>
          </label>
          <textarea
            id="ticket-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            maxLength={MESSAGE_MAX}
            aria-required
            aria-invalid={submitted && !!messageError}
            aria-describedby="ticket-message-help"
            placeholder="What happened, what you expected, and any steps to reproduce it."
            className={cn(
              textareaClasses,
              submitted && messageError && 'border-danger focus:border-danger/70 focus:ring-danger/10'
            )}
          />
          <p
            id="ticket-message-help"
            className={cn(
              'mt-1 text-xs',
              submitted && messageError ? 'text-danger' : 'text-gray-500 dark:text-gray-400'
            )}
          >
            {submitted && messageError
              ? messageError
              : `${trimmedMessage.length}/${MESSAGE_MAX}`}
          </p>
        </div>
      </form>
    </Modal>
  )
}

NewTicketModal.displayName = 'NewTicketModal'
